import React, { ReactNode } from "react";

import THEME from "styles/theme";
import Overlay from "components/shared/overlay";
import StyledMenuButton, { ButtonColor } from "components/shared/menu_button";
import Icon, { ICON_TYPE, ICON_COLOR } from "components/shared/icon";
import useOnEscapeKey from "helpers/hooks/useOnEscapeKey";
import styled from "styles/styled_components";

export enum MODAL_SIZE {
  STANDARD,
  LARGE,
}

interface IModalProps {
  size?: MODAL_SIZE;
}

const StyledModal = styled("div")<IModalProps>`
  background-color: ${props => props.theme.colors.brandGray10};
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.25);
  left: 0px;
  margin: 0 auto;
  max-height: 100vh;
  overflow-y: auto;
  position: fixed;
  right: 0px;
  text-align: center;
  top: 3vh;
  width: ${props => {
    switch (props.size) {
      case MODAL_SIZE.STANDARD:
        return `calc(48 * ${props.theme.gridBase})`;

      case MODAL_SIZE.LARGE:
        return `calc(75 * ${props.theme.gridBase})`;

      default:
        return `calc(55 * ${props.theme.gridBase})`;
    }
  }};
  z-index: ${props => props.theme.layering.modal};
`;

const StyledHeader = styled.header`
  background-color: ${props => props.theme.colors.brandWhite};
  border-top-left-radius: ${props => props.theme.borderRadius};
  border-top-right-radius: ${props => props.theme.borderRadius};
  display: flex;
  flex-direction: row;
  font-size: ${props => props.theme.text.size.large};
  justify-content: space-between;
  padding: ${props => props.theme.spacing.small};
  text-align: center;
  & h2 {
    line-height: ${props => props.theme.text.lineHeight.jumbo};
  }
`;

const StyledModalBody = styled.div`
  padding: ${props =>
    `${props.theme.spacing.medium} ${props.theme.spacing.large}`};
  position: relative;
  background-color: ${props => props.theme.colors.brandWhite};
`;

const StyledIconPlaceholder = styled.div`
  width: ${props => props.theme.iconSize.small};
`;

interface IModal {
  children: ReactNode;
  title: string;
  onClose: () => void;
  size?: MODAL_SIZE;
}

export default function Modal({
  title,
  children,
  onClose,
  size = MODAL_SIZE.STANDARD,
}: IModal) {
  useOnEscapeKey(onClose);

  return (
    <Overlay onClick={onClose}>
      <StyledModal size={size}>
        <StyledHeader>
          <StyledMenuButton
            borderless
            buttonColor={ButtonColor.WHITE}
            onClick={onClose}
            aria-label={`Close ${title} dialog`}>
            <Icon
              color={ICON_COLOR.GRAY}
              size={THEME.iconSize.tiny}
              type={ICON_TYPE.X}
            />
          </StyledMenuButton>
          <h2>{title}</h2>
          <StyledIconPlaceholder />
        </StyledHeader>
        <StyledModalBody>{children}</StyledModalBody>
      </StyledModal>
    </Overlay>
  );
}
